import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { agentsApi, companiesApi, skillsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { useNotification } from "../context/NotificationContext";
import EmployeeRosterCard from "../components/EmployeeRosterCard";
import EmployeeAvatar, { STATUS_META } from "../components/EmployeeAvatar";
import { detailText } from "../utils/apiErrors";
import { isUnrestrictedAllowlist } from "../utils/toolAllowlist";

const EMPTY_FORM = {
  name: "",
  description: "",
  client_id: "",
  model: "",
  system_prompt: "",
  autonomy_level: "approval_required",
  permission_tier: "tier_1",
};

// Sentinel for "explicitly-unrestricted allowlist" in the capability filter -- NULL/[]
// allowlists are a distinct, honest bucket (see toolAllowlist.js), not an empty one.
const UNRESTRICTED_CAPABILITY = "__unrestricted__";

// The routing-recommendation request is free text; these mirror the endpoint's own bounds
// (RoutingRecommendationRequest: 1..2000 chars) and its ranked-pool display head.
const ASK_REQUEST_MAX_LENGTH = 2000;
const ASK_ROUTER_VISIBLE_CANDIDATES = 5;

export function NewAgentModal({ companies, companiesFailed = false, saving, error, onSave, onCancel }) {
  const [form, setForm] = useState(EMPTY_FORM);

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.client_id) delete payload.client_id;
    if (!payload.description) delete payload.description;
    if (!payload.system_prompt) delete payload.system_prompt;
    onSave(payload);
  };

  // No Escape handler previously -- a keyboard user had to tab to the "ביטול" button.
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        onSubmit={submit}
        role="dialog" aria-modal="true" aria-labelledby="new-agent-modal-title"
        className="bg-white rounded-xl shadow-xl border p-6 w-[32rem] max-h-[90vh] overflow-y-auto space-y-4"
        dir="rtl"
      >
        <h3 id="new-agent-modal-title" className="text-base font-semibold text-gray-800">סוכן חדש</h3>

        <div>
          <label className="block text-xs text-gray-500 mb-1">שם *</label>
          <input
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="לפיילוט: AI Technician Pilot"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">תיאור</label>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">חברה</label>
          <select
            value={form.client_id}
            onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">ללא חברה</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {companiesFailed && (
            // "no company" would create a GLOBAL agent while the customer list is unknown
            <p role="alert" className="mt-1 text-xs text-red-600">לא ניתן לטעון את רשימת החברות — יצירת סוכן חסומה עד לטעינה מחדש</p>
          )}
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">מודל *</label>
          <input
            required
            value={form.model}
            onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
            placeholder="לדוגמה: claude-haiku-4-5-20251001"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">System prompt</label>
          <textarea
            rows={4}
            value={form.system_prompt}
            onChange={(e) => setForm((f) => ({ ...f, system_prompt: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">רמת הרשאה (Permission Tier)</label>
          <select
            value={form.permission_tier}
            onChange={(e) => setForm((f) => ({ ...f, permission_tier: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="tier_1">קריאה בלבד — ברירת מחדל</option>
            <option value="tier_2">תפעולי — פעולות עם אישור</option>
            <option value="tier_3">מוגבה — אישור חזק נדרש</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">רמת אוטונומיה</label>
          <select
            value={form.autonomy_level}
            onChange={(e) => setForm((f) => ({ ...f, autonomy_level: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            <option value="approval_required">דורש אישור (ברירת מחדל)</option>
            <option value="supervised">מפוקח</option>
            <option value="semi_autonomous">חצי אוטונומי</option>
            <option value="autonomous">אוטונומי מלא</option>
          </select>
        </div>

        {error && <p className="text-red-600 text-sm">{error?.response?.data?.detail || "שגיאה בשמירת הסוכן"}</p>}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving || companiesFailed}
            className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {saving ? "שומר..." : "צור סוכן"}
          </button>
          <button type="button" onClick={onCancel} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            ביטול
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Ask the manager (read-only routing recommendation) ──────────────────────
// The operator describes a general request and the manager answers who should take it and why.
// The endpoint behind this panel
// (POST /api/agents/routing-recommendation) only reads: it assigns nothing, starts nothing and
// sends nothing, and this panel mirrors that -- it never navigates on its own and never calls
// anything except the recommendation read. What happens next is the operator's decision, so the
// result is presented as a recommendation and never as an automatic assignment.

export function ManagerAskRouter({ agents, scoreMap }) {
  const [requestText, setRequestText] = useState("");
  const [result, setResult] = useState(null);

  const askMutation = useMutation({
    mutationFn: (request) =>
      api.post("/api/agents/routing-recommendation", { request }).then((r) => ({ ...r.data, request })),
    onMutate: () => setResult(null),
    onSuccess: (data) => setResult(data),
  });

  const trimmed = requestText.trim();
  const canSubmit = trimmed.length > 0 && !askMutation.isPending;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    askMutation.mutate(trimmed);
  };

  const recommended = result?.recommended || null;
  // Identity is rendered from the roster payload (the authoritative card fields); the
  // recommendation payload only ranks and explains, so the card can never misstate a policy field.
  const recommendedAgent = recommended ? agents.find((a) => a.id === recommended.agent_id) : null;
  // The pending request travels with the navigation into the employee page, so "continue the
  // request there" is more than re-opening the roster entry the operator just used.
  const continueState = result ? { askRouterRequest: result.request } : undefined;
  const ranked = result && !recommended ? result.candidates.slice(0, ASK_ROUTER_VISIBLE_CANDIDATES) : [];

  return (
    <section
      aria-label="שאלו את המנהל"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div>
        <h2 className="text-base font-semibold text-gray-800">🎯 שאלו את המנהל</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          תארו בקשה כללית — המנהל יציע איזה עובד דיגיטלי מתאים לטפל בה ומה הסיבה.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-2">
        <label htmlFor="manager-ask-request" className="block text-xs text-gray-500">
          הבקשה שלך
        </label>
        <textarea
          id="manager-ask-request"
          rows={2}
          maxLength={ASK_REQUEST_MAX_LENGTH}
          value={requestText}
          onChange={(e) => setRequestText(e.target.value)}
          placeholder="לדוגמה: מי יכול לעזור ללקוח עם בעיית ESET?"
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {askMutation.isPending ? "מחפש המלצה..." : "קבלו המלצה"}
        </button>
      </form>

      {askMutation.isError && (
        <p role="alert" className="text-sm text-red-600">
          {detailText(askMutation.error?.response?.data?.detail, "לא ניתן לקבל המלצה כעת")}
        </p>
      )}

      {result && (
        <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-3 space-y-3">
          <p role="note" className="text-xs text-gray-600">
            המלצה בלבד — לא בוצעה הקצאה אוטומטית, לא הופעלה הרצה ולא נשלחה הודעה. ההחלטה מה לעשות עם
            הפנייה היא שלכם.
          </p>
          <p className="text-xs text-gray-500">עבור הפנייה: "{result.request}"</p>

          {recommended ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-800">{result.recommendation_reason}</p>
              {recommendedAgent ? (
                <EmployeeRosterCard
                  agent={recommendedAgent}
                  score={scoreMap[recommendedAgent.id]}
                  canManage={false}
                />
              ) : (
                <div className="bg-white rounded-xl border shadow-sm p-4 flex items-center gap-3">
                  <EmployeeAvatar name={recommended.name} size="md" status={recommended.effective_availability} />
                  <div className="min-w-0">
                    <Link
                      to={`/agents/${recommended.agent_id}`}
                      state={continueState}
                      className="font-semibold text-brand-600 hover:underline"
                    >
                      {recommended.name}
                    </Link>
                    {recommended.role_title && (
                      <p className="text-xs text-gray-500 truncate">{recommended.role_title}</p>
                    )}
                  </div>
                </div>
              )}
              <Link
                to={`/agents/${recommended.agent_id}`}
                state={continueState}
                className="inline-block border border-brand-600 text-brand-600 hover:bg-brand-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                המשיכו את הפנייה במרחב העבודה ←
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-700">{result.recommendation_reason}</p>
              {ranked.length > 0 && (
                <>
                  <p className="text-xs font-medium text-gray-500">עובדים זמינים לפי סדר ההתאמה:</p>
                  <ul className="space-y-1.5">
                    {ranked.map((candidate) => (
                      <li
                        key={candidate.agent_id}
                        className="bg-white rounded-lg border px-3 py-2 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <EmployeeAvatar name={candidate.name} size="sm" status={candidate.effective_availability} />
                          <div className="min-w-0">
                            <Link
                              to={`/agents/${candidate.agent_id}`}
                              state={continueState}
                              className="text-sm font-medium text-brand-600 hover:underline truncate"
                            >
                              {candidate.name}
                            </Link>
                            {candidate.role_title && (
                              <p className="text-[11px] text-gray-500 truncate">{candidate.role_title}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-[11px] text-gray-500 flex-shrink-0 text-left">
                          <div>{STATUS_META[candidate.effective_availability]?.label || candidate.effective_availability}</div>
                          <div>עומס פעיל: {candidate.active_workload}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {result.considered_count > 0 && (
                <p className="text-[11px] text-gray-400">נבדקו {result.considered_count} עובדים זמינים.</p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function AgentsPage() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [showModal, setShowModal] = useState(false);
  const [expandedReadinessAgent, setExpandedReadinessAgent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [capabilityFilter, setCapabilityFilter] = useState("all");
  const { can: canAgent } = useAgentPermissions();
  const canManage = canAgent("agents_manage");
  // The ask-the-manager panel reads the same grant the recommendation endpoint enforces
  // (agents_view) -- asking is a read, not a management action.
  const canView = canAgent("agents_view");

  const { data: agents = [], isLoading, isError } = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
  });

  const { data: crisisMode, isError: crisisFailed } = useQuery({
    queryKey: ["crisis-mode"],
    queryFn: agentsApi.getCrisisMode,
    enabled: canManage,
    refetchInterval: 30000,
  });
  // A failed request is "unknown", never "not active" (the true state may be active): the activate button stays
  // (idempotent, the safe direction) but no reassuring status is claimed.
  const crisisActive = !crisisFailed && (crisisMode?.active ?? false);

  const [confirmCrisis, setConfirmCrisis] = useState(false);

  const activateCrisisMutation = useMutation({
    mutationFn: agentsApi.activateCrisisMode,
    onSuccess: () => {
      addNotification("🚨 מצב חירום הופעל — כל פעולות ה-AI חסומות", "warning");
      setConfirmCrisis(false);
      qc.invalidateQueries({ queryKey: ["crisis-mode"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בהפעלת מצב חירום", "error"),
  });
  const deactivateCrisisMutation = useMutation({
    mutationFn: agentsApi.deactivateCrisisMode,
    onSuccess: () => {
      addNotification("מצב חירום בוטל — פעולות AI חזרו לפעילות", "success");
      qc.invalidateQueries({ queryKey: ["crisis-mode"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בביטול מצב חירום", "error"),
  });

  const { data: autoTrigger, isError: autoTriggerFailed } = useQuery({
    queryKey: ["auto-trigger"],
    queryFn: agentsApi.getAutoTrigger,
    enabled: canManage,
    refetchInterval: 60000,
  });
  const autoTriggerActive = !autoTriggerFailed && (autoTrigger?.active ?? false);

  const { data: entitlementReadiness } = useQuery({
    queryKey: ["entitlement-readiness"],
    queryFn: agentsApi.entitlementReadiness,
    enabled: canManage,
    staleTime: 60000,
  });

  const enableAutoTriggerMutation = useMutation({
    mutationFn: agentsApi.enableAutoTrigger,
    onSuccess: () => {
      addNotification("הפעלה אוטומטית הופעלה", "success");
      qc.invalidateQueries({ queryKey: ["auto-trigger"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בהפעלת הטריגר האוטומטי", "error"),
  });
  const disableAutoTriggerMutation = useMutation({
    mutationFn: agentsApi.disableAutoTrigger,
    onSuccess: () => {
      addNotification("הפעלה אוטומטית הושבתה", "info");
      qc.invalidateQueries({ queryKey: ["auto-trigger"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בהשבתת הטריגר האוטומטי", "error"),
  });

  const { data: companies = [], isError: companiesFailed } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
  });

  const { data: leaderboard = [] } = useQuery({
    queryKey: ["skills-leaderboard"],
    // wrapped: react-query passes its context object to a function reference, and it landed in the request as query params
    queryFn: () => skillsApi.leaderboard(),
    staleTime: 120000,
  });
  // Only a report with outcome evidence carries a score; everything else is left out of the map so
  // the roster card's own missing-score path renders instead of a no-evidence baseline.
  const scoreMap = Object.fromEntries(
    leaderboard
      .filter((r) => r.has_outcome_evidence === true)
      .map((r) => [r.agent_id, r.overall_score])
  );

  const capabilityOptions = useMemo(() => {
    const names = new Set();
    agents.forEach((agent) => {
      if (Array.isArray(agent.allowed_tools)) agent.allowed_tools.forEach((tool) => names.add(tool));
    });
    return Array.from(names).sort();
  }, [agents]);

  const hasUnrestrictedEmployees = useMemo(
    () => agents.some((agent) => isUnrestrictedAllowlist(agent.allowed_tools)),
    [agents]
  );

  const filtersActive =
    Boolean(searchQuery.trim()) || statusFilter !== "all" || roleFilter !== "all" || capabilityFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setRoleFilter("all");
    setCapabilityFilter("all");
  };

  const filteredAgents = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return agents.filter((agent) => {
      if (statusFilter === "active" && !agent.is_enabled) return false;
      if (statusFilter === "disabled" && agent.is_enabled) return false;
      if (roleFilter !== "all" && agent.permission_tier !== roleFilter) return false;
      if (capabilityFilter !== "all") {
        if (capabilityFilter === UNRESTRICTED_CAPABILITY) {
          if (!isUnrestrictedAllowlist(agent.allowed_tools)) return false;
        } else if (!Array.isArray(agent.allowed_tools) || !agent.allowed_tools.includes(capabilityFilter)) {
          return false;
        }
      }
      if (query) {
        const haystack = [
          agent.name,
          agent.description,
          agent.client_name,
          agent.model,
          ...(Array.isArray(agent.allowed_tools) ? agent.allowed_tools : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [agents, searchQuery, statusFilter, roleFilter, capabilityFilter]);

  const activeCount = agents.filter((agent) => agent.is_enabled).length;
  const disabledCount = agents.length - activeCount;
  const pendingRunsTotal = agents.reduce((sum, agent) => sum + (agent.pending_runs_count || 0), 0);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["agents"] });
    // agents-dash/agents-layout-badge/agents-dash-filter/cc-agents all poll agentsApi.list() under
    // their own keys with no shared prefix with ["agents"] -- creating or toggling an agent here
    // used to leave those pages showing the old roster until their own refetchInterval (or, for
    // agents-dash-filter, which has none, until its staleTime) fires. Mirrors AgentDetailPage.jsx's
    // own updateMutation, which already invalidates the same set for an edit from that page.
    qc.invalidateQueries({ queryKey: ["agents-dash"] });
    qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
    qc.invalidateQueries({ queryKey: ["agents-dash-filter"] });
    qc.invalidateQueries({ queryKey: ["cc-agents"] });
  };

  const createMutation = useMutation({
    mutationFn: agentsApi.create,
    onSuccess: () => { addNotification("סוכן נוצר בהצלחה", "success"); setShowModal(false); refresh(); },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת סוכן", "error"),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_enabled }) => agentsApi.update(id, { is_enabled }),
    onSuccess: (_data, variables) => {
      addNotification(variables?.is_enabled ? "הסוכן הופעל" : "הסוכן הושבת", "info");
      refresh();
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשינוי מצב הסוכן", "error"),
  });

  return (
    <div className="space-y-5 max-w-6xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">הצוות הדיגיטלי</h1>
          <p className="text-sm text-gray-500 mt-0.5">העובדים שלך — זהות, תפקיד, מצב ויכולות במבט אחד</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Link
              to="/employee-builder"
              className="border border-brand-600 text-brand-600 hover:bg-brand-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              🧙 אשף יצירה
            </Link>
            <button
              onClick={() => setShowModal(true)}
              className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              + עובד חדש
            </button>
          </div>
        )}
      </div>

      {canManage && (
        <div className={`rounded-xl border px-4 py-3 flex items-center justify-between ${crisisActive ? "bg-red-50 border-red-300" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${crisisActive ? "text-red-700" : "text-gray-600"}`}>
              {crisisActive
                ? "⚠️ מצב חירום פעיל — כל פעולות הכתיבה של הסוכנים מושהות"
                : crisisFailed ? "מצב חירום: לא ניתן לטעון את הסטטוס — המצב לא ידוע" : "מצב חירום: לא פעיל"}
            </span>
          </div>
          {crisisActive ? (
            <button
              onClick={() => deactivateCrisisMutation.mutate()}
              disabled={deactivateCrisisMutation.isPending}
              className="text-xs px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-medium"
            >
              {deactivateCrisisMutation.isPending ? "..." : "בטל מצב חירום"}
            </button>
          ) : confirmCrisis ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-700 font-medium">בטוח? פעולות הכתיבה יושהו</span>
              <button
                onClick={() => activateCrisisMutation.mutate()}
                disabled={activateCrisisMutation.isPending}
                className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                {activateCrisisMutation.isPending ? "..." : "אישור"}
              </button>
              <button
                onClick={() => setConfirmCrisis(false)}
                className="text-xs px-2 py-1.5 text-gray-500 hover:text-gray-700"
              >
                ביטול
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmCrisis(true)}
              className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-red-50 hover:border-red-300 hover:text-red-700 font-medium"
            >
              הפעל מצב חירום
            </button>
          )}
        </div>
      )}

      {canManage && (
        <div className={`rounded-xl border px-4 py-3 flex items-center justify-between ${autoTriggerActive ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${autoTriggerActive ? "text-blue-700" : "text-gray-600"}`}>
              {autoTriggerActive
                ? "🤖 ניתוח AI אוטומטי פעיל — קריאות חדשות מנותחות אוטומטית"
                : autoTriggerFailed ? "ניתוח AI אוטומטי: לא ניתן לטעון את הסטטוס — המצב לא ידוע" : "ניתוח AI אוטומטי: כבוי (מצב ידני)"}
            </span>
          </div>
          {autoTriggerFailed ? null : autoTriggerActive ? (
            <button
              onClick={() => disableAutoTriggerMutation.mutate()}
              disabled={disableAutoTriggerMutation.isPending}
              className="text-xs px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 font-medium"
            >
              {disableAutoTriggerMutation.isPending ? "..." : "כבה אוטומטי"}
            </button>
          ) : (
            <button
              onClick={() => enableAutoTriggerMutation.mutate()}
              disabled={enableAutoTriggerMutation.isPending}
              className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 font-medium"
            >
              {enableAutoTriggerMutation.isPending ? "..." : "הפעל ניתוח אוטומטי"}
            </button>
          )}
        </div>
      )}

      {canManage && entitlementReadiness && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="text-sm font-semibold text-gray-700">
              🔐 מוכנות אכיפת הרשאות (Tool Registry)
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                entitlementReadiness.enforcement_currently_enabled
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-gray-50 text-gray-500 border-gray-200"
              }`}
              title="TOOL_ENTITLEMENT_ENFORCEMENT_ENABLED — כבוי כברירת מחדל; אינו נאכף כל עוד לא הופעל במפורש"
            >
              אכיפה: {entitlementReadiness.enforcement_currently_enabled ? "פעילה" : "כבויה"}
            </span>
          </div>
          <div className="divide-y">
            {entitlementReadiness.agents.map((a) => (
              <div key={a.agent_id} className="px-4 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-gray-700 truncate">{a.agent_name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 uppercase flex-shrink-0">
                      {a.permission_tier}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                        a.enforcement_ready
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}
                    >
                      {a.enforcement_ready ? "✓ מוכן" : "⚠ חסום"}
                    </span>
                    <button
                      type="button"
                      onClick={() => setExpandedReadinessAgent(expandedReadinessAgent === a.agent_id ? null : a.agent_id)}
                      className="text-xs text-brand-600 hover:underline"
                    >
                      {expandedReadinessAgent === a.agent_id ? "הסתר" : "פרטים"}
                    </button>
                  </div>
                </div>
                {!a.enforcement_ready && (
                  <p className="text-xs text-amber-600 mt-1">
                    יידחו אם האכיפה תופעל: {a.blocked_write_actions.join(", ")}
                  </p>
                )}
                {expandedReadinessAgent === a.agent_id && (
                  <div className="mt-2 border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr>
                          <th className="text-right px-2 py-1.5">כלי</th>
                          <th className="text-right px-2 py-1.5">דרגה נדרשת</th>
                          <th className="text-right px-2 py-1.5">זכאות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.tools.map((t) => (
                          <tr key={t.name} className="border-t">
                            <td className="px-2 py-1.5 font-mono" dir="ltr">
                              {t.name}
                              {a.write_actions.includes(t.name) && (
                                <span className="mr-1 text-[9px] text-blue-500">●</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-gray-500">{t.required_tiers.join(", ")}</td>
                            <td className="px-2 py-1.5">
                              <span
                                className={t.entitled ? "text-green-600" : "text-red-500"}
                                title={t.reason || undefined}
                              >
                                {t.entitled ? "✓ זכאי" : "✗ לא זכאי"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[10px] text-gray-400 px-2 py-1.5 bg-gray-50 border-t">
                      ● = אחת מ-4 הפעולות שהפיילוט מבצע בפועל
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : isError ? (
        <div className="py-12 text-center text-red-600">לא ניתן לטעון סוכנים</div>
      ) : agents.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-600 font-medium">עדיין אין עובדים דיגיטליים</p>
          <p className="text-sm text-gray-400 mt-1">
            עובדים דיגיטליים מטפלים בקריאות, משוחחים עם טכנאים ולקוחות ומייצרים ריצות עבודה — כולם יופיעו כאן.
          </p>
          {canManage ? (
            <Link
              to="/employee-builder"
              className="inline-block mt-4 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              🧙 צור עובד דיגיטלי
            </Link>
          ) : (
            <p className="text-sm text-gray-400 mt-3">כדי להוסיף עובד דיגיטלי, פנה למנהל המערכת.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {canView && <ManagerAskRouter agents={agents} scoreMap={scoreMap} />}

          <div className="bg-white rounded-xl border shadow-sm p-3 space-y-2" role="search" aria-label="סינון העובדים הדיגיטליים">
            <div className="flex flex-col md:flex-row gap-2">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="חיפוש לפי שם, תיאור, חברה, מודל או כלי..."
                aria-label="חיפוש עובד דיגיטלי"
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="סינון לפי מצב"
                className="border rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="all">כל המצבים</option>
                <option value="active">פעילים בלבד</option>
                <option value="disabled">מושבתים בלבד</option>
              </select>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                aria-label="סינון לפי תפקיד"
                className="border rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="all">כל התפקידים</option>
                <option value="tier_1">T1 — עובד קריאה</option>
                <option value="tier_2">T2 — עובד תפעולי</option>
                <option value="tier_3">T3 — עובד מוגבה</option>
              </select>
              <select
                value={capabilityFilter}
                onChange={(e) => setCapabilityFilter(e.target.value)}
                aria-label="סינון לפי יכולת"
                className="border rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="all">כל היכולות</option>
                {capabilityOptions.map((tool) => (
                  <option key={tool} value={tool}>{tool}</option>
                ))}
                {hasUnrestrictedEmployees && (
                  <option value={UNRESTRICTED_CAPABILITY}>ללא הגבלת כלים</option>
                )}
              </select>
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-gray-500">
              {/* The counts double as next actions: a state count filters the roster to it, and pending
                  work opens the pre-filtered runs dashboard instead of dead-ending as text. */}
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <button
                  type="button"
                  onClick={() => setStatusFilter("active")}
                  aria-label="הצג עובדים פעילים"
                  className="hover:text-brand-600 hover:underline"
                >
                  פעילים: {activeCount}
                </button>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  onClick={() => setStatusFilter("disabled")}
                  aria-label="הצג עובדים מושבתים"
                  className="hover:text-brand-600 hover:underline"
                >
                  מושבתים: {disabledCount}
                </button>
                <span aria-hidden="true">·</span>
                {pendingRunsTotal > 0 ? (
                  <Link
                    to="/agent-runs?status=pending_review"
                    aria-label={`הרצות ממתינות לאישור: ${pendingRunsTotal} — לטיפול`}
                    className="text-orange-700 font-medium hover:underline"
                  >
                    הרצות ממתינות לאישור: {pendingRunsTotal} ←
                  </Link>
                ) : (
                  <span>הרצות ממתינות לאישור: 0</span>
                )}
              </span>
              {filtersActive && (
                <span className="flex items-center gap-2">
                  <span>מציג {filteredAgents.length} מתוך {agents.length} עובדים</span>
                  <button type="button" onClick={clearFilters} className="text-brand-600 hover:underline font-medium">
                    נקה סינון
                  </button>
                </span>
              )}
            </div>
          </div>

          {filteredAgents.length === 0 ? (
            <div className="text-center py-12 space-y-2">
              <p className="text-gray-500">לא נמצאו עובדים דיגיטליים שמתאימים לסינון</p>
              <button type="button" onClick={clearFilters} className="text-sm text-brand-600 hover:underline font-medium">
                הצג את כל העובדים
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredAgents.map((agent) => (
                <EmployeeRosterCard
                  key={agent.id}
                  agent={agent}
                  score={scoreMap[agent.id]}
                  canManage={canManage}
                  toggling={toggleMutation.isPending && toggleMutation.variables?.id === agent.id}
                  onToggle={() => toggleMutation.mutate({ id: agent.id, is_enabled: !agent.is_enabled })}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {canManage && showModal && (
        <NewAgentModal
          companies={companies}
          companiesFailed={companiesFailed}
          saving={createMutation.isPending}
          error={createMutation.error}
          onSave={(data) => createMutation.mutate(data)}
          onCancel={() => { createMutation.reset(); setShowModal(false); }}
        />
      )}
    </div>
  );
}
